import {
  INTRANET_COMMENT_GIF_MAX_BYTES,
  INTRANET_REACTION_EMOJI,
  INTRANET_COMMENT_MAX,
  INTRANET_FILE_ACCEPT,
  INTRANET_IMAGE_ACCEPT,
  INTRANET_SEARCH_MIN_QUERY,
  intranetCommentAttachmentPath,
  intranetImageMaxMb,
  type IntranetColleague,
  type IntranetComment,
  type IntranetCommentAttachment,
  type IntranetCommentReply,
  type IntranetContentDetail,
} from '@gb-ticket/shared-types';
import {
  ImageIcon,
  PaperclipIcon,
  SendHorizontalIcon,
  SmileIcon,
  SmilePlusIcon,
  XIcon,
} from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { useVoice } from '@/platform/voice/use-voice';
import {
  ColleagueNameButton,
  colleagueVisibleName,
} from '@/products/intranet/components/colleague-name-button';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import { IntranetLikeNames } from '@/products/intranet/components/intranet-like-names';
import {
  IntranetReactionPicker,
  isIntranetReactionOverlay,
} from '@/products/intranet/components/intranet-reaction-picker';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import {
  insertMention,
  mentionQueryAt,
} from '@/products/intranet/features/intranet/comment-mention';
import {
  useAddIntranetComment,
  useLockIntranetComments,
  useRemoveIntranetComment,
  useSetIntranetCommentLike,
  useSetIntranetCommentReaction,
} from '@/products/intranet/features/intranet/use-intranet-ack';
import { useIntranetAppearance } from '@/products/intranet/features/intranet/use-intranet';
import { useIntranetColleagues } from '@/products/intranet/features/intranet/use-intranet-directory';

const MENTION_DEBOUNCE_MS = 300;

/** Comment files follow the intranet list without video. Video stays on the chat composer. */
const COMMENT_FILE_ACCEPT = INTRANET_FILE_ACCEPT.split(',')
  .filter((extension) => extension !== '.mp4' && extension !== '.webm')
  .join(',');

const COMMENT_EMOJIS = [
  '😀',
  '😁',
  '😂',
  '😊',
  '😍',
  '😎',
  '🤔',
  '😢',
  '👍',
  '👎',
  '👏',
  '🙏',
  '🎉',
  '❤️',
  '🔥',
  '✅',
] as const;

type MentionPick = { userId: string; displayName: string };
type ReplyTarget = { id: string; displayName: string };

export function CommentsSection({
  content,
  canEdit,
}: {
  content: IntranetContentDetail;
  canEdit: boolean;
}) {
  const voice = useVoice();
  const add = useAddIntranetComment(content.slug);
  const lock = useLockIntranetComments(content.slug);
  const appearance = useIntranetAppearance();
  const gifMaxBytes = appearance.data?.commentGifMaxBytes ?? INTRANET_COMMENT_GIF_MAX_BYTES;
  const [body, setBody] = useState('');
  const [caret, setCaret] = useState(0);
  const [mentions, setMentions] = useState<MentionPick[]>([]);
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  const [composing, setComposing] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const imageRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const gifRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLDivElement>(null);
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionQ, setMentionQ] = useState('');
  const mentionOpen = mentionQ.length >= INTRANET_SEARCH_MIN_QUERY;
  const colleagues = useIntranetColleagues({ q: mentionQ }, mentionOpen);
  const total = countComments(content.comments);
  const canWrite = !content.commentsLocked && !content.isDraft;
  const activeReplyTo =
    replyTo && canWrite && content.comments.some((comment) => comment.id === replyTo.id)
      ? replyTo
      : null;
  const toolsOpen =
    composing || Boolean(activeReplyTo) || body.length > 0 || Boolean(file) || emojiOpen;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  function assignFile(next: File | null) {
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      if (next?.type.startsWith('image/')) {
        return URL.createObjectURL(next);
      }
      return null;
    });
    setFile(next);
  }

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id.startsWith('kommentar-')) {
      return;
    }
    document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
  }, [content.comments]);

  useEffect(() => {
    const handle = window.setTimeout(
      () =>
        setMentionQ(
          mentionQuery && mentionQuery.length >= INTRANET_SEARCH_MIN_QUERY ? mentionQuery : '',
        ),
      MENTION_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [mentionQuery]);

  useEffect(() => {
    if (!emojiOpen) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (emojiPanelRef.current?.contains(target) || emojiButtonRef.current?.contains(target)) {
        return;
      }
      setEmojiOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [emojiOpen]);

  function insertEmoji(emoji: string) {
    const at = caret;
    const end = at + emoji.length;
    syncMention(`${body.slice(0, at)}${emoji}${body.slice(at)}`, end);
    setEmojiOpen(false);
    window.requestAnimationFrame(() => {
      const field = document.getElementById('intranet-comment');
      if (!(field instanceof HTMLTextAreaElement)) {
        return;
      }
      field.focus();
      field.setSelectionRange(end, end);
    });
  }

  function syncMention(value: string, nextCaret: number) {
    setBody(value);
    setCaret(nextCaret);
    setMentionQuery(mentionQueryAt(value, nextCaret));
  }

  function pickColleague(colleague: IntranetColleague) {
    const visibleName = colleagueVisibleName(colleague.displayName, voice);
    const next = insertMention(body, caret, visibleName);
    setMentions((current) =>
      current.some((item) => item.userId === colleague.id)
        ? current
        : [...current, { userId: colleague.id, displayName: visibleName }],
    );
    syncMention(next.value, next.caret);
    setMentionQuery(null);
    setMentionQ('');
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const text = body.trim();
    if (!text && !file) {
      return;
    }
    const mentionUserIds = mentions
      .filter((item) => text.includes(`@${item.displayName}`))
      .map((item) => item.userId);
    try {
      await add.mutateAsync({
        body: text,
        ...(activeReplyTo ? { parentId: activeReplyTo.id } : {}),
        ...(mentionUserIds.length > 0 ? { mentionUserIds } : {}),
        ...(file ? { file } : {}),
      });
      setBody('');
      assignFile(null);
      setEmojiOpen(false);
      setMentions([]);
      setReplyTo(null);
      setMentionQuery(null);
      setComposing(false);
      toast.success(activeReplyTo ? 'Antwort gespeichert.' : 'Kommentar gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Kommentar fehlgeschlagen.');
    }
  }

  function pickFile(list: FileList | null) {
    const next = list?.[0];
    if (!next) {
      return;
    }
    if (next.type === 'image/gif' && next.size > gifMaxBytes) {
      toast.error(`Das GIF darf höchstens ${intranetImageMaxMb(gifMaxBytes)} MB groß sein.`);
      return;
    }
    assignFile(next);
    setComposing(true);
  }

  async function toggleLock() {
    try {
      await lock.mutateAsync({ locked: !content.commentsLocked });
      toast.success(
        content.commentsLocked ? 'Kommentare wieder geöffnet.' : 'Kommentare gesperrt.',
      );
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Aktion fehlgeschlagen.');
    }
  }

  function startReply(target: ReplyTarget) {
    setReplyTo(target);
    setComposing(true);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  }

  function renderComposer() {
    return (
      <form
        className="space-y-2"
        onSubmit={(event) => void onSubmit(event)}
        onFocusCapture={() => setComposing(true)}
        onBlurCapture={(event) => {
          const related = event.relatedTarget;
          if (related instanceof Node && event.currentTarget.contains(related)) {
            return;
          }
          if (!body.trim() && !file && !activeReplyTo && !emojiOpen) {
            setComposing(false);
          }
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="intranet-comment" className={activeReplyTo ? undefined : 'sr-only'}>
            {activeReplyTo ? `Antwort an ${activeReplyTo.displayName}` : 'Kommentar schreiben'}
          </Label>
          {activeReplyTo ? (
            <button
              type="button"
              className="min-h-11 text-sm text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              onClick={() => setReplyTo(null)}
            >
              Antwort abbrechen
            </button>
          ) : null}
        </div>
        <input
          ref={imageRef}
          type="file"
          accept={INTRANET_IMAGE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-label="Bild wählen"
          onChange={(event) => {
            pickFile(event.target.files);
            event.target.value = '';
          }}
        />
        <input
          ref={fileRef}
          type="file"
          accept={COMMENT_FILE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-label="Datei wählen"
          onChange={(event) => {
            pickFile(event.target.files);
            event.target.value = '';
          }}
        />
        <input
          ref={gifRef}
          type="file"
          accept="image/gif,.gif"
          className="sr-only"
          tabIndex={-1}
          aria-label="GIF wählen"
          onChange={(event) => {
            pickFile(event.target.files);
            event.target.value = '';
          }}
        />
        <div
          className={cn(
            'relative rounded-2xl border border-border bg-muted/40 shadow-none transition-[box-shadow,border-color] duration-200 dark:bg-muted/40',
            toolsOpen && 'border-primary/60 ring-2 ring-ring/70',
          )}
        >
          <Textarea
            ref={textareaRef}
            id="intranet-comment"
            rows={toolsOpen ? 3 : 2}
            maxLength={INTRANET_COMMENT_MAX}
            placeholder={
              activeReplyTo
                ? `Antwort an ${activeReplyTo.displayName} …`
                : 'Schreibe einen Kommentar …'
            }
            value={body}
            className="field-sizing-fixed min-h-11 resize-none rounded-2xl border-0 bg-transparent px-4 py-2.5 shadow-none focus-visible:ring-0 dark:bg-transparent"
            onChange={(event) => syncMention(event.target.value, event.target.selectionStart ?? 0)}
            onSelect={(event) => {
              const next = event.currentTarget.selectionStart ?? 0;
              setCaret(next);
              setMentionQuery(mentionQueryAt(body, next));
            }}
          />
          {file ? (
            <div className="flex items-center gap-2 px-3 pb-2">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={file.name}
                  className="h-14 w-14 rounded-md object-cover"
                />
              ) : (
                <span className="truncate text-xs text-muted-foreground">{file.name}</span>
              )}
              <button
                type="button"
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                aria-label="Anhang entfernen"
                onClick={() => assignFile(null)}
              >
                <XIcon className="size-4" aria-hidden />
              </button>
            </div>
          ) : null}
          <div
            className="grid transition-[grid-template-rows,opacity] duration-200 ease-out"
            data-open={toolsOpen ? 'true' : 'false'}
            style={{
              gridTemplateRows: toolsOpen ? '1fr' : '0fr',
              opacity: toolsOpen ? 1 : 0,
            }}
          >
            <div
              className={cn('min-h-0 overflow-hidden', !toolsOpen && 'invisible')}
              inert={!toolsOpen}
            >
              <div className="flex items-center gap-0.5 px-2 pb-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-9 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="Bild hinzufügen"
                  disabled={add.isPending}
                  onClick={() => imageRef.current?.click()}
                >
                  <ImageIcon aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-9 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="Anhang"
                  disabled={add.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  <PaperclipIcon aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 shrink-0 px-2 text-xs font-semibold tracking-wide text-muted-foreground hover:text-foreground"
                  disabled={add.isPending}
                  onClick={() => gifRef.current?.click()}
                >
                  GIF
                </Button>
                <div ref={emojiButtonRef} className="relative">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-9 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label="Emoji"
                    aria-expanded={emojiOpen}
                    disabled={add.isPending}
                    onClick={() => setEmojiOpen((open) => !open)}
                  >
                    <SmileIcon aria-hidden />
                  </Button>
                </div>
                <Button
                  type="submit"
                  size="icon"
                  className="ml-auto size-9 shrink-0 rounded-full"
                  aria-label="Senden"
                  disabled={add.isPending || (!body.trim() && !file)}
                >
                  <SendHorizontalIcon aria-hidden />
                </Button>
              </div>
            </div>
          </div>
          {emojiOpen ? (
            <div
              ref={emojiPanelRef}
              className="absolute top-full left-2 z-50 mt-1 grid w-52 grid-cols-8 gap-1 rounded-md border bg-popover p-2 shadow-md"
              onMouseDown={(event) => event.preventDefault()}
            >
              {COMMENT_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded p-1 text-base hover:bg-muted"
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
          {mentionOpen ? (
            <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border bg-popover shadow-md">
              {colleagues.isPending ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  Kollegen werden gesucht …
                </li>
              ) : (colleagues.data?.items.length ?? 0) === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  Keine Kollegin oder kein Kollege.
                </li>
              ) : (
                colleagues.data?.items.map((colleague) => (
                  <li key={colleague.id}>
                    <button
                      type="button"
                      className="flex min-h-11 w-full items-center px-3 text-left text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                      onClick={() => pickColleague(colleague)}
                    >
                      {colleagueVisibleName(colleague.displayName, voice)}
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>
      </form>
    );
  }

  return (
    <section
      aria-labelledby="kommentare"
      className="rounded-xl border border-border bg-card shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-xl border-b border-border px-4 py-3 sm:px-6">
        <h2 id="kommentare" className="text-base font-semibold text-foreground">
          Kommentare ({total})
        </h2>
        {canEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={lock.isPending}
            onClick={() => void toggleLock()}
          >
            {content.commentsLocked ? 'Kommentare öffnen' : 'Kommentare sperren'}
          </Button>
        ) : null}
      </div>

      <div className="space-y-4 p-4 sm:p-6">
        {content.commentCount > total ? (
          <p className="text-sm text-muted-foreground">
            Es werden die letzten {total} von {content.commentCount} Kommentaren angezeigt.
          </p>
        ) : null}

        {content.commentsLocked ? (
          <p className="text-sm text-muted-foreground">Die Kommentare sind geschlossen.</p>
        ) : content.isDraft ? (
          <p className="text-sm text-muted-foreground">
            Kommentare sind erst nach der Veröffentlichung möglich.
          </p>
        ) : !activeReplyTo ? (
          renderComposer()
        ) : null}

        <div className={cn(canWrite && !activeReplyTo && 'border-t border-border pt-4')}>
          {total === 0 && !activeReplyTo ? (
            <p className="text-sm text-muted-foreground">Noch keine Kommentare.</p>
          ) : (
            <ul className="space-y-4">
              {content.comments.map((comment) => {
                const replyingHere = activeReplyTo?.id === comment.id;
                const replyCount = comment.replies.length;
                return (
                  <li key={comment.id} id={`kommentar-${comment.id}`}>
                    <div className="relative">
                      {replyCount > 0 || replyingHere ? (
                        <span
                          aria-hidden
                          className="pointer-events-none absolute top-[38px] bottom-0 left-[18px] w-px -translate-x-1/2 bg-foreground/25"
                        />
                      ) : null}
                      <CommentBody
                        slug={content.slug}
                        comment={comment}
                        canReply={canWrite}
                        canModerate={canEdit}
                        reactionsOn={content.reactionsEnabled !== false}
                        onReply={() =>
                          startReply({
                            id: comment.id,
                            displayName: comment.author.displayName,
                          })
                        }
                      />
                    </div>
                    {replyCount > 0 || replyingHere ? (
                      <ul aria-label={`Antworten auf ${comment.author.displayName}`}>
                        {replyingHere ? (
                          <li className="relative pl-10">
                            <span
                              aria-hidden
                              className={cn(
                                'pointer-events-none absolute left-[18px] w-px -translate-x-1/2 bg-foreground/25',
                                replyCount > 0 ? 'inset-y-0' : 'top-0 h-8',
                              )}
                            />
                            <span
                              aria-hidden
                              className="pointer-events-none absolute top-8 left-[18px] h-px w-[22px] bg-foreground/25"
                            />
                            <div className="pt-3">{renderComposer()}</div>
                          </li>
                        ) : null}
                        {comment.replies.map((reply, index) => {
                          const last = index === comment.replies.length - 1;
                          return (
                            <li
                              key={reply.id}
                              id={`kommentar-${reply.id}`}
                              className="relative pl-10"
                            >
                              <span
                                aria-hidden
                                className={cn(
                                  'pointer-events-none absolute left-[18px] w-px -translate-x-1/2 bg-foreground/25',
                                  last ? 'top-0 h-8' : 'inset-y-0',
                                )}
                              />
                              <span
                                aria-hidden
                                className="pointer-events-none absolute top-8 left-[18px] h-px w-[22px] bg-foreground/25"
                              />
                              <div className="pt-3">
                                <CommentBody
                                  slug={content.slug}
                                  comment={reply}
                                  canReply={false}
                                  canModerate={canEdit}
                                  reactionsOn={content.reactionsEnabled !== false}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function CommentBody({
  slug,
  comment,
  canReply,
  canModerate,
  reactionsOn,
  onReply,
}: {
  slug: string;
  comment: IntranetComment | IntranetCommentReply;
  canReply: boolean;
  canModerate: boolean;
  reactionsOn: boolean;
  onReply?: () => void;
}) {
  const me = useMe();
  const like = useSetIntranetCommentLike(slug);
  const react = useSetIntranetCommentReaction(slug);
  const remove = useRemoveIntranetComment(slug);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const currentKind = comment.myReaction ?? (comment.likedByMe ? 'like' : null);
  const own = me.data?.id === comment.author.id;
  const hasReplies = 'replies' in comment && comment.replies.length > 0;
  const canDelete = (own || canModerate) && !hasReplies;

  useEffect(() => {
    if (!pickerOpen) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (
        !(target instanceof Node) ||
        pickerRef.current?.contains(target) ||
        isIntranetReactionOverlay(target)
      ) {
        return;
      }
      setPickerOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [pickerOpen]);

  async function toggleLike() {
    if (window.matchMedia?.('(pointer: coarse)').matches) {
      setPickerOpen(true);
      return;
    }
    try {
      await like.mutateAsync({ id: comment.id, liked: !comment.likedByMe });
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Gefällt mir fehlgeschlagen.');
    }
  }

  async function onRemove() {
    try {
      await remove.mutateAsync(comment.id);
      setConfirmOpen(false);
      toast.success('Kommentar gelöscht.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  const time = formatCommentAgo(comment.createdAt);

  return (
    <div className="flex items-start gap-2.5">
      <ColleagueNameButton
        userId={comment.author.id}
        displayName={comment.author.displayName}
        className="mt-0.5 shrink-0 hover:no-underline"
      >
        <ColleaguePortrait
          displayName={comment.author.displayName}
          profilePhotoUrl={comment.author.profilePhotoUrl ?? null}
          className="size-9 text-xs"
        />
      </ColleagueNameButton>
      <div className="min-w-0 flex-1">
        <div className="w-fit max-w-[min(100%,32rem)] rounded-2xl bg-muted/70 px-3 py-2">
          <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
            <ColleagueNameButton
              userId={comment.author.id}
              displayName={comment.author.displayName}
              className="font-semibold text-foreground"
            />
            <time
              className="text-muted-foreground"
              dateTime={comment.createdAt}
              title={time.absolute}
            >
              {time.label}
            </time>
          </p>
          {comment.body ? (
            <p className="mt-1 text-sm leading-snug whitespace-pre-wrap text-foreground">
              {comment.body}
            </p>
          ) : null}
          {comment.attachment ? <CommentAttachmentView attachment={comment.attachment} /> : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1 px-1">
          {reactionsOn ? (
            <>
              <div
                ref={pickerRef}
                className="relative py-1"
                onMouseEnter={() => setPickerOpen(true)}
              >
                <button
                  type="button"
                  aria-pressed={comment.likedByMe}
                  aria-label={`Gefällt mir, ${comment.likeCount}`}
                  disabled={like.isPending}
                  className={cn(
                    'inline-flex min-h-11 items-center gap-1 rounded-md px-1.5 text-base hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    comment.likedByMe ? 'text-primary' : 'text-muted-foreground',
                  )}
                  onClick={() => void toggleLike()}
                >
                  <SmilePlusIcon className="size-4" aria-hidden />
                  {comment.likeCount > 0 ? (
                    <span className="text-xs">{comment.likeCount}</span>
                  ) : null}
                </button>
                {pickerOpen ? (
                  <IntranetReactionPicker
                    current={currentKind}
                    anchor={pickerRef}
                    onPick={(kind) => {
                      setPickerOpen(false);
                      void react
                        .mutateAsync({ id: comment.id, reaction: kind })
                        .catch((error: unknown) => {
                          toast.error(
                            error instanceof ApiRequestError
                              ? error.message
                              : 'Die Reaktion konnte nicht gespeichert werden.',
                          );
                        });
                    }}
                  />
                ) : null}
              </div>
              <div className="ml-auto">
                <IntranetLikeNames
                  count={comment.likeCount}
                  emojis={(comment.reactions ?? []).map(
                    (row) => INTRANET_REACTION_EMOJI[row.reaction],
                  )}
                  listPath={`/intranet/comments/${encodeURIComponent(comment.id)}/reactions`}
                />
              </div>
            </>
          ) : null}
          {canReply && onReply ? (
            <button
              type="button"
              className="min-h-11 rounded-md px-1.5 text-xs text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              onClick={onReply}
            >
              Antworten
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              disabled={remove.isPending}
              className="min-h-11 rounded-md px-1.5 text-xs text-destructive hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              onClick={() => setConfirmOpen(true)}
            >
              Löschen
            </button>
          ) : null}
          <ConfirmDeleteDialog
            open={confirmOpen}
            title="Kommentar löschen?"
            description="Der Kommentar wird dauerhaft entfernt. Das lässt sich nicht rückgängig machen."
            pending={remove.isPending}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => void onRemove()}
          />
        </div>
      </div>
    </div>
  );
}

function CommentAttachmentView({ attachment }: { attachment: IntranetCommentAttachment }) {
  if (attachment.scanStatus === 'PENDING') {
    return <p className="mt-2 text-xs text-muted-foreground">Wird geprüft …</p>;
  }
  if (attachment.scanStatus !== 'CLEAN' && attachment.scanStatus !== 'SKIPPED') {
    return <p className="mt-2 text-xs text-muted-foreground">Datei blockiert.</p>;
  }
  const src = `${API_BASE_URL}${intranetCommentAttachmentPath(attachment.id)}`;
  if (attachment.kind === 'image' || attachment.kind === 'gif') {
    return (
      <img src={src} alt={attachment.filename} className="mt-2 max-h-56 max-w-full rounded-lg" />
    );
  }
  if (attachment.downloadable === false) {
    return <p className="mt-2 text-sm">{attachment.filename}</p>;
  }
  return (
    <a href={src} className="mt-2 inline-flex text-sm text-primary underline">
      {attachment.filename}
    </a>
  );
}

function formatCommentAgo(iso: string, now = new Date()): { label: string; absolute: string } {
  const absolute = formatDateTime(iso);
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) {
    return { label: 'gerade eben', absolute };
  }
  if (minutes < 60) {
    return { label: `vor ${minutes} Min.`, absolute };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { label: `vor ${hours} Std.`, absolute };
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return { label: `vor ${days} Tg.`, absolute };
  }
  return { label: absolute, absolute };
}

function countComments(comments: readonly IntranetComment[]): number {
  return comments.reduce((sum, comment) => sum + 1 + comment.replies.length, 0);
}
